import { useState, useEffect } from "react"
import { getDatabase, ref, onValue } from "firebase/database"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { Info, Filter, ChevronDown, ChevronUp, MapPin, Building, DollarSign, Truck, RefreshCw, Map, IndianRupee } from "lucide-react"
import "./RateHistory.css"
import { BsExclamationCircle } from "react-icons/bs"

const RateHistory = () => {
  const [quotes, setQuotes] = useState([])
  const [showQuotes, setShowQuotes] = useState(false)
  const [selectedRegion, setSelectedRegion] = useState("")
  const [selectedArea, setSelectedArea] = useState("")
  const [pricingInfo, setPricingInfo] = useState({})
  const [activeAccordion, setActiveAccordion] = useState(null)
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");


  const regionsWithAreas = {
    Punjab: [
      "Chandigarh",
      "Hoshiarpur",
      "Kharad",
      "Jallandhar",
      "Mohali",
      "Gurdaspur",
      "Pathankot",
      "Ludhiana",
      "Amritsar",
      "Patiala",
      "Firozpur",
      "Batala",
      "Bathinda",
      "Kapurthala",
      "Barnala",
    ],
    Himachal: [
      "Kangra",
      "Rampur Shimla",
      "Baddi",
      "Kinnour",
      "Kasauli",
      "Parwanu",
      "Sirmour",
      "Poanta sahib",
      "Solan",
      "Nalagarh",
      "Una",
      "Sahran",
      "Rohru",
      "Kullu",
      "Mandi",
      "Hamirpur",
      "Chamba",
      "Nurpur",
      "Bilashpur",
    ],
    Uttrakhand: [
      "Dehradun khuthal gate",
      "Rurki",
      "Haridwar",
      "Rishikesh",
      "Saharanpur",
      "Laksar",
      "vikasnagar",
      "Bijnour",
      "Heldwani",
      "Chamoli",
    ],
    Jharkhand: ["Jharkhand"],
    Jammu: ["Srinagar"],
    Haryana: ["Ambala", "Panchhkula", "fatehpur"],
  }

  useEffect(() => {
    const auth = getAuth();
    const db = getDatabase();
    const connectedRef = ref(db, ".info/connected");

    // Offline check
    if (!navigator.onLine) {
      setSessionError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setSessionError("Session expired. Please login again.");
        setLoading(false);
        return;
      }
      setSessionError("");
      const userId = user.uid;
      const quotesRef = ref(db, `rates/${userId}`);

      // Firebase connection check
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setSessionError("Session expired. Please login again.");
          setLoading(false);
          return;
        }
        setSessionError("");
        // Fetch user rates
        const unsubscribeRates = onValue(quotesRef, (snapshot) => {
          const quotesData = snapshot.val();
          if (quotesData) {
            const quotesArray = Object.keys(quotesData).map((key) => ({
              id: key,
              ...quotesData[key],
            }));
            setQuotes(quotesArray);
          } else {
            setQuotes([]);
          }
          setLoading(false);
        });

        // Cleanup Firebase data listener
        return () => unsubscribeRates();
      });
    });

    // Cleanup auth listener
    return () => unsubscribeAuth();
  }, []);


  // Fetch pricing information based on selected region and area
  useEffect(() => {
    if (selectedRegion && selectedArea) {
      const pricingRef = ref(getDatabase(), `rates/${selectedRegion}/${selectedArea}`)
      const unsubscribe = onValue(pricingRef, (snapshot) => {
        const pricingData = snapshot.val()
        if (pricingData) {
          setPricingInfo(pricingData)
        } else {
          setPricingInfo({})
        }
      })

      return () => {
        unsubscribe()
      }
    }
  }, [selectedRegion, selectedArea])

  // Toggle quotes display
  const handleUpdateClick = () => {
    setShowQuotes(!showQuotes)
  }

  // Handler for selecting region
  const handleRegionChange = (event) => {
    setSelectedRegion(event.target.value)
    setSelectedArea("") // Reset area when region changes
    setPricingInfo({}) // Clear pricing info when region changes
  }

  // Handler for selecting area
  const handleAreaChange = (event) => {
    setSelectedArea(event.target.value)
  }

  const quoteInfo = () => {
    alert("Select a region and area to view pricing information.")
  }

  const toggleAccordion = (index) => {
    if (activeAccordion === index) {
      setActiveAccordion(null)
    } else {
      setActiveAccordion(index)
    }
  }

  const faqItems = [
    {
      question: "How do I get pricing information?",
      answer:
        "Select a region and area from the dropdown menus, and the pricing information for each brand and grade will appear in the cards below.",
    },
    {
      question: "Are the prices listed here final?",
      answer:
        "The prices shown are estimates based on selected brands and grades. For exact rates, please proceed with a formal quote request or contact support.",
    },
    {
      question: "How are delivery times calculated?",
      answer:
        "Delivery times are estimated based on the selected region and area. They may vary based on stock availability and logistics.",
    },
    {
      question: "Can I get rates for multiple regions or areas?",
      answer:
        "Yes, you can select different regions and areas to view and compare pricing information for various locations.",
    },
    {
      question: "How often is the pricing information updated?",
      answer:
        "Pricing information is updated regularly. However, prices may fluctuate based on market conditions. For the most current rates, request a formal quote.",
    },
    {
      question: "What do I do if I don't see my area listed?",
      answer:
        "If your area is not listed, please contact our support team (9876980108). We'll assist you in obtaining rates for unlisted areas.",
    },
    {
      question: "What factors affect the pricing of cement?",
      answer:
        "Pricing can be influenced by factors like grade, brand, region, and logistical considerations such as distance and delivery requirements.",
    },
    {
      question: "Who can I contact for more detailed assistance?",
      answer:
        "For further assistance, please reach out to our customer support team (9876980108). They can provide personalized help with your requirements.",
    },
  ]
  if (loading) {
    return <div className="loading-container"><div className="loader"></div></div>;
  }

  if (sessionError) {
    return <div className="error">{sessionError}</div>;
  }

  return (
    <div className="rate-container">
      <div className="rate-card">
        <div className="rate-header">
          <h2 className="text-white">Current Cement Rates</h2>
          <Info className="info-icon" onClick={quoteInfo} />
        </div>

        <div className="rate-body">
          <div className="location-selector">
            <div className="select-wrapper">
              <Map className="select-icon" />
              <select className="custom-select" value={selectedRegion} onChange={handleRegionChange}>
                <option value="">--Select Region--</option>
                {Object.keys(regionsWithAreas).map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>

            <div className="select-wrapper">
              <MapPin className="select-icon" />
              <select
                className="custom-select"
                value={selectedArea}
                onChange={handleAreaChange}
                disabled={!selectedRegion}
              >
                <option value="">--Select Area--</option>
                {selectedRegion &&
                  regionsWithAreas[selectedRegion].map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Pricing Information Section */}
          <div className="pricing-section">
            <div className="pricing-header">
              <h3>Pricing Information</h3>
              <Filter className="filter-icon" />
            </div>

            {selectedRegion && selectedArea ? (
              <div className="pricing-grid">
                {pricingInfo && Object.keys(pricingInfo).length > 0 ? (
                  Object.keys(pricingInfo).flatMap((brand) =>
                    Object.keys(pricingInfo[brand]).map((grade) => (
                      <div className="price-card"  key={`${brand}-${grade}`}>
                        <div className="price-card-header">
                          <Building className="brand-icon"/>
                          <h4>{brand || "Updates soon..."}</h4>
                        </div>
                        <div className="price-card-body">
                          <div className="price-detail">
                            <span className="detail-label">Grade:</span>
                            <span className="detail-value">{grade}</span>
                          </div>
                          <div className="price-detail">
                            <span className="detail-label">
                              <IndianRupee className="detail-icon" />
                              Price:
                            </span>
                            <span className="detail-value price">₹ {pricingInfo[brand][grade].price}</span>
                          </div>
                          <div className="price-detail">
                            <span className="detail-label">
                              <Truck className="detail-icon" />
                              Delivery:
                            </span>
                            <span className="detail-value">{pricingInfo[brand][grade].deliveryTime}</span>
                          </div>
                        </div>
                      </div>
                    )),
                  )
                ) : (
                  <div className="no-data-message">
                    <RefreshCw className="no-data-icon" />
                    <p>No pricing information available for this location.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="select-prompt">
                <Info className="prompt-icon" />
                <p>Please select a region and area to view pricing information.</p>
              </div>
            )}
          </div>
          <div className="desclaimer-prompt">
            <BsExclamationCircle className="desclaim-icon" />
            <p className="text-primary mt-2">Prices are subject to the company's prevailing rates. Please confirm the applicable rates prior to placing an order.</p>
          </div>

          {/* FAQs Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>
            <div className="accordion">
              {faqItems.map((item, index) => (
                <div className="accordion-item" key={index}>
                  <button
                    className={`accordion-button ${activeAccordion === index ? "active" : ""}`}
                    onClick={() => toggleAccordion(index)}
                  >
                    <span>{item.question}</span>
                    {activeAccordion === index ? (
                      <ChevronUp className="accordion-icon" />
                    ) : (
                      <ChevronDown className="accordion-icon" />
                    )}
                  </button>
                  <div className={`accordion-content ${activeAccordion === index ? "active" : ""}`}>
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RateHistory;